import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Text } from '@chakra-ui/react';
import { useUser } from '../contexts/UserContext';
import { useConfig } from '../contexts/ConfigContext';
import { getCachedUserProfile, isSubscriptionExpired } from '../utils';
import { USER_TYPES } from '../constants';

interface PaidPeriodGuardProps {
  children: React.ReactNode;
}

export function PaidPeriodGuard({ children }: PaidPeriodGuardProps) {
  const { user } = useUser();
  const { config } = useConfig();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    // Admin users are not subject to paid period checks
    if (user?.userType === USER_TYPES.ADMIN) {
      setChecking(false);
      setExpired(false);
      return;
    }

    const checkSubscription = async () => {
      if (!config?.apiUrl || !user?.id) {
        setChecking(false);
        return;
      }

      try {
        const profileData = await getCachedUserProfile(config.apiUrl, user.id, user.userType);
        setExpired(isSubscriptionExpired(profileData.data?.paidTillDate));
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        // Fail closed: treat error as expired to prevent unauthorized access
        setExpired(true);
      } finally {
        setChecking(false);
      }
    };

    checkSubscription();
  }, [user, config]);

  if (checking) {
    return (
      <Box minH="100vh" bg="#120F1B" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="xl" color="#C6D959">Loading...</Text>
      </Box>
    );
  }

  if (expired) {
    return <Navigate to="/subscription-expired" replace />;
  }

  return <>{children}</>;
}
